import styled from '@emotion/styled';
import { ui } from '@libs/domain';
import { useItemNames } from '@ps-smart-cp/ppfw-client';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import {
  buildRedirectUri,
  FormSection,
  fetchSsoStatus,
  fetchUserPoolInfo,
  initOrganizationState,
  LoginContainer,
  LoginCredentials,
  LoginForm,
  LoginFormSsoRequired,
  StyledLoginButton,
  signIn,
} from '../features/login';
import { useTryShell } from '../hooks';
import type { OrganizationState } from '../types';

const Separator = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  text-align: center;
  margin: 20px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #ccc;
  }

  &::before {
    margin-right: 10px;
  }

  &::after {
    margin-left: 10px;
  }
`;

// AUTH-333 エラーメッセージのローカライズ
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tryRequest, error, setError } = useTryShell();

  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('id') ?? '';

  const state = location.state as OrganizationState | null;
  const [organizationState, setOrganizationState] = useState(initOrganizationState(state));
  const [credentials, setCredentials] = useState(LoginCredentials.init(organizationId));

  const titleNames = useItemNames(ui.titleNames);
  const buttonNames = useItemNames(ui.buttonNames);

  useEffect(() => {
    if (organizationState.organizationName === '' || organizationState.ssoStatus === null) {
      const fetchData = async () => {
        await tryRequest(
          async () => {
            setOrganizationState(await fetchSsoStatus(organizationId));
          },
          () => navigate('/', { replace: true }),
        );
      };

      void fetchData();
    }
  }, [organizationState]);

  const onLogin = async () =>
    await tryRequest(async () => {
      const validateError =
        credentials.organizationId === '' || credentials.username === '' || credentials.password === ''
          ? '【ログインエラー】指定された組織ID、ユーザーID、パスワードに一致するアカウントがありません。(EFW56001)'
          : null;
      if (validateError) {
        setError(validateError);
        return;
      }

      const res = await signIn(credentials, false);

      if (res.challengeName === 'NEW_PASSWORD_REQUIRED') {
        navigate('/user/change-password/first-time', {
          state: {
            region: res.region,
            challengeName: res.challengeName,
            session: res.session,
            organizationId: res.organizationId,
            username: res.username,
          },
        });
      } else {
        navigate(`/${res.region}/main-menu`);
      }
    });

  const onContinueWithSso = async () =>
    await tryRequest(async () => {
      const validateError =
        organizationId === ''
          ? '【ログインエラー】SSOで続けるには、組織IDを入力してください(EFW56002)'
          : null;
      if (validateError) {
        setError(validateError);
        return;
      }

      const res = await fetchUserPoolInfo(organizationId);

      const { cognitoDomain, clientId, idpName } = res;
      const OAUTH_ENDPOINT = `/oauth2/authorize?client_id=${clientId}&response_type=code&scope=aws.cognito.signin.user.admin+email+openid+profile&redirect_uri=${buildRedirectUri()}&identity_provider=${idpName}`;

      sessionStorage.setItem('organizationId', organizationId);

      window.location.href = `${cognitoDomain}${OAUTH_ENDPOINT}`;
    });

  return (
    <LoginContainer title={titleNames('login')} error={error}>
      {organizationState.ssoStatus?.required ? (
        <LoginFormSsoRequired
          credentials={credentials}
          organizationName={organizationState.organizationName}
          ssoStatus={organizationState.ssoStatus}
          onContinueWithSso={onContinueWithSso}
        />
      ) : (
        <>
          <LoginForm
            credentials={credentials}
            setCredentials={setCredentials}
            organizationName={organizationState.organizationName}
            ssoStatus={organizationState.ssoStatus}
            onLogin={onLogin}
          />
          {organizationState.ssoStatus &&
            (organizationState.ssoStatus.oidc === 'enabled' ||
              organizationState.ssoStatus.saml === 'enabled') && (
              <FormSection>
                <Separator>or</Separator>
                <StyledLoginButton
                  id="sso-btn"
                  displayName={buttonNames('continueWithSso')}
                  onClick={onContinueWithSso}
                />
              </FormSection>
            )}
        </>
      )}
    </LoginContainer>
  );
}
